import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'theme';
import 'utils/sentry';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { fontUri, muiTheme } from 'theme/mui/theme.mui';
import { CssBaseline, CircularProgress, Stack } from '@mui/material';

import dayjs from 'dayjs';
import dayjsUtc from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';

import queryClient from 'query.client';
import { useWhitelabelSettings } from 'modules/branches/hooks';
import AppRouter from 'AppRouter';
import { googleFontUri } from 'theme/theme';
import ErrorBoundary from 'components/v2/alert/ErrorBoundary';
import { AuthApi } from 'api';
import { useAuthRedirect } from 'hooks/services/useAuthRedirect';

// Add timezone plugin to dayjs
dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);

const MOUSEFLOW_ID = import.meta.env.VITE_MOUSEFLOW_ID;

const shouldRenderGTM = import.meta.env.VITE_DEPLOYMENT_ENV === 'production' && !!import.meta.env.VITE_GTM_CONTAINER;

function AppContent() {
  const { data: whitelabel } = useWhitelabelSettings(AuthApi.isAuthenticated());
  const { isAuthenticated, userIsLoading } = useAuthRedirect();

  if (isAuthenticated && userIsLoading) {
    return (
      <Stack direction="row" height="100vh" justifyContent="center" alignItems="center">
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <>
      <Helmet>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <link rel="shortcut icon" href={whitelabel?.faviconUrl || '/fav-icon-32.png'} />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link href={fontUri} rel="stylesheet" />
        <link href={googleFontUri} rel="stylesheet" />
        <script
          id="CookieConsent"
          src="https://policy.app.cookieinformation.com/uc.js"
          data-culture="DA"
          type="text/javascript"
        />
        {shouldRenderGTM && (
          <script>
            {`
						(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
						new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
						j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
						'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
						})(window,document,'script','dataLayer','${import.meta.env.VITE_GTM_CONTAINER}');
					`}
          </script>
        )}
      </Helmet>
      {/* Google Tag Manager (noscript) */}
      {shouldRenderGTM && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${import.meta.env.VITE_GTM_CONTAINER}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          ></iframe>
        </noscript>
      )}
      {/* End Google Tag Manager (noscript) */}
      <ThemeProvider
        primaryColor={whitelabel?.primaryColor}
        contrastColor={whitelabel?.contrastColor}
        pageBackgroundColor={whitelabel?.pageBackgroundColor}
      >
        <Sentry.ErrorBoundary fallback={ErrorBoundary} showDialog>
          <MuiThemeProvider theme={muiTheme}>
            <ToastContainer />
            <CssBaseline />
            <AppRouter />
          </MuiThemeProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
      {MOUSEFLOW_ID && (
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
						window._mfq = window._mfq || [];
						(function() {
							var mf = document.createElement("script");
							mf.type = "text/javascript"; mf.defer = true;
							mf.src = "//cdn.mouseflow.com/projects/${MOUSEFLOW_ID}.js";
							document.getElementsByTagName("head")[0].appendChild(mf);
						})();
					`,
          }}
        />
      )}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
